import Phaser from 'phaser';
import { GAME_FONT_FAMILY } from '@shared/constants';
import { getSpellDefinition } from '@shared/data/spells';
import { getSummonDisplayForSpell } from '@shared/utils/summonDisplay';
import type { InputManager, ActionKeyId } from '../../input/InputManager';

const SLOT_COUNT = 10;
const PLACEHOLDER_ICON = 'item_icon_noicon';
/** Caja del keycap (numerito de tecla) = asset main_shortcut_frame */
const KEYCAP_FRAME_KEY = 'main_shortcut_frame';
/** Fallback size until setLayout() is called by GameScene with the HUD geometry */
const DEFAULT_SLOT_SIZE = 46;
/** Fracción del slot que ocupa el icono del skill (sin caja extra, solo gloss) */
const ICON_FRAC = 1.0;
/** Color del número del keycap (crema claro sobre la caja oscura del asset). */
const KEYCAP_TEXT = '#ece6d6';
/** Efectos de slot (assets ui/main) */
const FX_OVERLAY_KEY = 'spell_frame_icon_OVERLAY';
const FX_HOVER_KEY = 'spell_frame_hover_effect';
const FX_PUSH_KEY = 'spell_frame_push_effect';
const FX_COOLDOWN_KEY = 'spell_frame_cooldown_effect';

/** Maps slot index → ActionKeyId in InputManager */
const SLOT_ACTION_IDS: ActionKeyId[] = [
  'spell1', 'spell2', 'spell3', 'spell4', 'spell5',
  'spell6', 'spell7', 'spell8', 'spell9', 'spell10',
];

/** Ms que hay que mantener apretado un slot para arrancar el drag (reordenar). */
const SLOT_DRAG_HOLD_MS = 150;

function withGameFont<T extends Phaser.Types.GameObjects.Text.TextStyle>(style?: T): T {
  return { fontFamily: GAME_FONT_FAMILY, resolution: 2, ...style } as T;
}

/** Converts key names like "ONE","TWO" to "1","2", passes others through */
const KEY_SHORT: Record<string, string> = {
  ONE: '1', TWO: '2', THREE: '3', FOUR: '4', FIVE: '5',
  SIX: '6', SEVEN: '7', EIGHT: '8', NINE: '9', ZERO: '0',
};
function toShortKeyLabel(label: string): string {
  return KEY_SHORT[label] ?? label;
}

interface SlotUI {
  frame: Phaser.GameObjects.Image | Phaser.GameObjects.Rectangle;
  icon: Phaser.GameObjects.Image;
  label: Phaser.GameObjects.Text;
  keyHint: Phaser.GameObjects.Text;
  keycapImg: Phaser.GameObjects.Image | null;
  iconOverlay: Phaser.GameObjects.Image | null;
  hoverFx: Phaser.GameObjects.Image | null;
  pushFx: Phaser.GameObjects.Image | null;
  cdImg: Phaser.GameObjects.Image | null;
  cdText: Phaser.GameObjects.Text;
  spellId: string | null;
  cdStart: number;
  cdDuration: number;
  // Absolute screen-space center + display size (set by setLayout)
  x: number;
  y: number;
  size: number; // alto del slot (y representativo para keycap/tooltip/hitbox vertical)
  width: number; // ancho del slot (puede diferir del alto: slot no-cuadrado)
}

type SpellQuickBarCallbacks = {
  onCastSpell(spellId: string): void;
  /** isSwap = movimiento/intercambio interno (no dispara cooldown de asignación). */
  onSetSlot(slotIndex: number, spellId: string, isSwap?: boolean): void;
};

/**
 * Barra rápida de hechizos (10 slots) montada sobre los recesos del nuevo HUD
 * (`main_frame`, skin ui/main). Cada slot usa `main_shortcut_frame` como fondo.
 * GameScene calcula la geometría (centros + tamaño) según el frame y la pasa por
 * `setLayout()` en `createHUD`/`updateHUDPositions`. Teclas configurables
 * (default 1..9,0) vía InputManager; labels se refrescan al cambiar keybindings.
 */
export class SpellQuickBar {
  private readonly scene: Phaser.Scene;
  private readonly callbacks: SpellQuickBarCallbacks;
  private readonly inputManager: InputManager;
  private slots: SlotUI[] = [];
  private container: Phaser.GameObjects.Container;
  private tooltip: Phaser.GameObjects.Container | null = null;
  private tooltipBg: Phaser.GameObjects.Rectangle | null = null;
  private tooltipText: Phaser.GameObjects.Text | null = null;

  // Drag interno slot↔slot (hold ≥ SLOT_DRAG_HOLD_MS): reordenar / intercambiar.
  private slotDrag: {
    fromIndex: number;
    active: boolean;
    preview: Phaser.GameObjects.Image | null;
    holdTimer: Phaser.Time.TimerEvent | null;
  } | null = null;

  constructor(scene: Phaser.Scene, callbacks: SpellQuickBarCallbacks, inputManager: InputManager) {
    this.scene = scene;
    this.callbacks = callbacks;
    this.inputManager = inputManager;

    this.container = scene.add.container(0, 0);
    this.container.setDepth(1010); // Above HUD (HUD_DEPTH = 1000)
    this.container.setScrollFactor(0);

    // Create slots at origin; real positions come from setLayout().
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = this.createSlot(i);
      this.slots.push(slot);
    }

    this.createTooltip();
    this.refreshKeyHints();
  }

  /**
   * Reposiciona los slots sobre los recesos del HUD. `positions` son centros en
   * coords absolutas de pantalla (scrollFactor 0), `size` el ALTO del slot en px y
   * `width` el ANCHO (default = size → slot cuadrado). El keycap y el hitbox vertical
   * usan el alto.
   */
  setLayout(positions: Array<{ x: number; y: number }>, size: number, width: number = size): void {
    for (let i = 0; i < SLOT_COUNT; i++) {
      const slot = this.slots[i];
      const p = positions[i];
      if (!p) continue;
      slot.x = p.x;
      slot.y = p.y;
      slot.size = size;
      slot.width = width;

      slot.frame.setPosition(p.x, p.y);
      if ('setDisplaySize' in slot.frame) {
        (slot.frame as Phaser.GameObjects.Image).setDisplaySize(width, size);
      } else {
        (slot.frame as Phaser.GameObjects.Rectangle).setSize(width, size);
      }

      // Overlay centrado en la caja del main_frame (= tamaño del slot).
      slot.iconOverlay?.setPosition(p.x, p.y).setDisplaySize(width, size);

      // Icono: 3px menos por lado que el overlay, centrado dentro del overlay.
      const iconW = width - 6;
      const iconH = size - 6;
      slot.icon.setPosition(p.x, p.y).setDisplaySize(iconW, iconH);
      slot.hoverFx?.setPosition(p.x, p.y).setDisplaySize(width * 1.04, size * 1.04);
      slot.pushFx?.setPosition(p.x, p.y).setDisplaySize(width * 1.1, size * 1.1);
      slot.cdImg?.setPosition(p.x, p.y).setDisplaySize(width, size);

      // Keycap (asset main_shortcut_frame) al pie-centro, solapando el borde inferior.
      const kc = Math.round(size * 0.46);
      const ky = p.y + size / 2; // centro del keycap sobre el borde inferior (50% abajo)
      slot.keycapImg?.setPosition(p.x, ky).setDisplaySize(kc, kc);
      // El número 1..0 llena la cajita del keycap (antes ~31% y no se veía).
      slot.keyHint.setPosition(p.x, ky).setFontSize(Math.max(14, Math.round(kc * 0.6) + 1));
    }
  }

  /**
   * Cast the spell in the given slot index. Called from InputSystem.
   */
  castSlot(index: number): void {
    if (index < 0 || index >= SLOT_COUNT) return;
    const slot = this.slots[index];
    if (slot.spellId) this.callbacks.onCastSpell(slot.spellId);
  }

  /**
   * Set a spell in a slot
   */
  setSlot(index: number, spellId: string | null): void {
    if (index < 0 || index >= SLOT_COUNT) return;
    const slot = this.slots[index];
    slot.spellId = spellId;

    const has = !!spellId;
    // Slot vacío = NADA (sin frame, sin icono, sin placeholder); solo queda el keycap.
    slot.frame.setVisible(has);
    slot.icon.setVisible(has);
    slot.iconOverlay?.setVisible(has);

    if (has) {
      const spell = getSpellDefinition(spellId);
      slot.label.setText(spell ? this.abbreviate(spell.nameEs, 5) : '?');
      const iconW = slot.width - 6;
      const iconH = slot.size - 6;
      const iconKey = (spell?.icon && this.scene.textures.exists(spell.icon)) ? spell.icon : PLACEHOLDER_ICON;
      slot.icon.setTexture(iconKey).setDisplaySize(iconW, iconH).setAlpha(1);
    } else {
      slot.label.setText('');
      slot.cdImg?.setVisible(false);
      slot.cdDuration = 0;
    }
  }

  /**
   * Assign the next empty slot (or slot 0 if all full).
   */
  assignToNextSlot(spellId: string): number {
    const existingIdx = this.slots.findIndex(s => s.spellId === spellId);
    if (existingIdx >= 0) return existingIdx;

    const emptyIdx = this.slots.findIndex(s => !s.spellId);
    const targetIdx = emptyIdx >= 0 ? emptyIdx : 0;

    this.setSlot(targetIdx, spellId);
    this.callbacks.onSetSlot(targetIdx, spellId);
    // Asignación nueva → arrancar cooldown optimista (el server lo reconcilia).
    this.startAssignCooldown(spellId);
    return targetIdx;
  }

  /**
   * Cooldown optimista al asignar un hechizo NUEVO a la barra (anti-flood). El
   * server es la fuente de verdad y lo reconcilia vía SPELL_BAR_SYNC.
   */
  startAssignCooldown(spellId: string): void {
    const spell = getSpellDefinition(spellId);
    if (spell && spell.cooldown > 0) this.startCooldown(spellId, spell.cooldown * 1000);
  }

  /** True si el hechizo ya está en algún slot de la barra. */
  hasSpell(spellId: string): boolean {
    return this.slots.some((s) => s.spellId === spellId);
  }

  /**
   * Sync all slots from server data. Incluye el cooldown restante por slot para
   * que el visual refleje el server (y no se "resetee" al re-syncar la barra).
   */
  syncSlots(data: Array<{ slotIndex: number; spellId: string; cooldownMs?: number }>): void {
    for (let i = 0; i < SLOT_COUNT; i++) this.setSlot(i, null);
    const now = Date.now();
    for (const { slotIndex, spellId, cooldownMs } of data) {
      this.setSlot(slotIndex, spellId);
      if (slotIndex < 0 || slotIndex >= SLOT_COUNT) continue;
      const slot = this.slots[slotIndex];
      if (cooldownMs && cooldownMs > 0) {
        // Reconstruir el cooldown con el total real del spell para que el drenado
        // muestre la fracción correcta (parcial), no un cooldown "fresco".
        const total = (getSpellDefinition(spellId)?.cooldown ?? 0) * 1000;
        if (total > 0) {
          slot.cdDuration = total;
          slot.cdStart = now - Math.max(0, total - cooldownMs);
        }
      }
    }
  }

  /**
   * Refresh key hint labels from InputManager (call after keybind changes)
   */
  refreshKeyHints(): void {
    for (let i = 0; i < SLOT_COUNT; i++) {
      const actionId = SLOT_ACTION_IDS[i];
      const keyLabel = this.inputManager.getActionKeyLabel(actionId);
      this.slots[i].keyHint.setText(toShortKeyLabel(keyLabel));
    }
  }

  /**
   * Start cooldown animation on a spell (called after successful cast)
   */
  startCooldown(spellId: string, durationMs: number): void {
    const now = Date.now();
    for (const slot of this.slots) {
      if (slot.spellId === spellId) {
        // Don't reset if already on cooldown
        if (slot.cdDuration > 0 && (now - slot.cdStart) < slot.cdDuration) continue;
        slot.cdStart = now;
        slot.cdDuration = durationMs;
      }
    }
  }

  /**
   * Update cooldown visuals (call from scene update loop)
   */
  update(): void {
    const now = Date.now();
    for (const slot of this.slots) {
      const cd = slot.cdImg;
      if (!cd) continue;

      if (slot.cdDuration <= 0 || !slot.spellId) {
        if (cd.visible) cd.setVisible(false);
        continue;
      }

      const remaining = slot.cdDuration - (now - slot.cdStart);
      if (remaining <= 0) {
        slot.cdDuration = 0;
        cd.setVisible(false);
        continue;
      }

      // Wipe vertical con el asset de cooldown: el gris drena de ARRIBA hacia ABAJO
      // (se despeja primero arriba) a medida que el CD termina.
      const frac = remaining / slot.cdDuration; // 1 = recién casteado, 0 = listo
      const h = cd.height; // alto nativo de la textura
      cd.setVisible(true);
      cd.setCrop(0, h * (1 - frac), cd.width, h * frac);
    }
  }

  /**
   * Check if a spell is currently on cooldown
   */
  isOnCooldown(spellId: string): boolean {
    const now = Date.now();
    for (const slot of this.slots) {
      if (slot.spellId === spellId && slot.cdDuration > 0) {
        if ((now - slot.cdStart) < slot.cdDuration) return true;
      }
    }
    return false;
  }

  isPointerOver(pointer: Phaser.Input.Pointer): boolean {
    return this.getSlotIndexAt(pointer) >= 0;
  }

  /** Índice del slot bajo el cursor, o -1 si ninguno. */
  getSlotIndexAt(pointer: Phaser.Input.Pointer): number {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i];
      const halfW = slot.width / 2;
      const halfH = slot.size / 2;
      if (
        pointer.x >= slot.x - halfW && pointer.x <= slot.x + halfW &&
        pointer.y >= slot.y - halfH && pointer.y <= slot.y + halfH
      ) {
        return i;
      }
    }
    return -1;
  }

  /** Tamaño (px) del slot actual — usado para dimensionar el preview de drag. */
  getSlotSize(): number {
    return this.slots[0]?.size ?? DEFAULT_SLOT_SIZE;
  }

  // === DRAG INTERNO slot↔slot ================================================

  /** Hay un drag de slot ACTIVO (preview siguiendo al cursor). */
  isDraggingSlot(): boolean { return !!this.slotDrag?.active; }
  /** Hay un drag activo o un hold pendiente (para rutear el pointerup global). */
  hasSlotDrag(): boolean { return !!this.slotDrag; }

  /** Reenvía el movimiento del cursor al preview de drag. */
  handlePointerMove(p: Phaser.Input.Pointer): void {
    if (this.slotDrag?.active && this.slotDrag.preview) {
      this.slotDrag.preview.setPosition(p.x, p.y);
    }
  }

  /** Resuelve el drop: mueve/intercambia el hechizo al slot bajo el cursor. */
  handlePointerUp(p?: Phaser.Input.Pointer): void {
    if (this.slotDrag?.active && p) {
      const from = this.slotDrag.fromIndex;
      const to = this.getSlotIndexAt(p);
      if (to >= 0 && to !== from) {
        const spellFrom = this.slots[from].spellId;
        const spellTo = this.slots[to].spellId;
        // Intercambio (o mover si el destino está vacío → spellTo = null limpia el origen).
        this.setSlot(to, spellFrom);
        this.setSlot(from, spellTo);
        // isSwap: es un movimiento interno, no una asignación nueva → sin cooldown.
        this.callbacks.onSetSlot(to, spellFrom ?? '', true);
        this.callbacks.onSetSlot(from, spellTo ?? '', true);
      }
    }
    this.cancelSlotDrag();
  }

  /** Activa el drag (llamado por el timer de hold): crea el preview grande. */
  private activateSlotDrag(index: number, p: Phaser.Input.Pointer): void {
    if (!this.slotDrag || this.slotDrag.fromIndex !== index) return;
    const spellId = this.slots[index].spellId;
    if (!spellId) { this.cancelSlotDrag(); return; }
    this.slotDrag.holdTimer = null;

    const spell = getSpellDefinition(spellId);
    const iconKey = spell?.icon && this.scene.textures.exists(spell.icon) ? spell.icon : PLACEHOLDER_ICON;
    const size = this.getSlotSize();
    const preview = this.scene.add
      .image(p.x, p.y, iconKey)
      .setOrigin(0.5)
      .setDisplaySize(size, size)
      .setDepth(1250)
      .setScrollFactor(0)
      .setAlpha(0.9);
    this.slotDrag.preview = preview;
    this.slotDrag.active = true;
    // Atenuar el ícono del slot de origen mientras se arrastra.
    this.slots[index].icon.setAlpha(0.35);
    this.hideTooltip();
  }

  /** Cancela cualquier drag/hold en curso y limpia el preview. */
  private cancelSlotDrag(): void {
    if (!this.slotDrag) return;
    this.slotDrag.holdTimer?.remove(false);
    this.slotDrag.preview?.destroy();
    // Restaurar el alpha del ícono de origen.
    const src = this.slots[this.slotDrag.fromIndex];
    if (src) src.icon.setAlpha(1);
    this.slotDrag = null;
  }

  // =========================================================================

  private createSlot(index: number): SlotUI {
    const size = DEFAULT_SLOT_SIZE;
    // Capas (de atrás a adelante): frame → icon → iconOverlay(gloss) → cooldown → hover/push → keycap.
    // frame/icon/overlay arrancan OCULTOS: un slot vacío no muestra nada (solo keycap).

    // Zona de input invisible (el slot configurado se ve por su icono + gloss, no por un frame).
    const frame = this.scene.add.rectangle(0, 0, size, size, 0x000000, 0.001);
    frame.setInteractive({ useHandCursor: true }).setVisible(false);
    this.container.add(frame);

    // SIEMPRE una Image (nunca un Rectangle). Si `item_icon_noicon` todavía no
    // está en la caché de texturas al construir la barra (los íconos se cargan de
    // forma asíncrona), un Rectangle NO tiene `setTexture`, así que `setSlot` nunca
    // podría pintar el ícono del hechizo y el slot quedaría vacío (bug histórico).
    // Con una Image, aunque el placeholder no exista aún, el `setTexture` de
    // `setSlot` pinta el ícono real. (La ventana H hace exactamente esto.)
    const icon = this.scene.add
      .image(0, 0, PLACEHOLDER_ICON)
      .setDisplaySize(size * ICON_FRAC, size * ICON_FRAC)
      .setVisible(false);
    this.container.add(icon);

    // Gloss del icono (spell_frame_icon_OVERLAY, marco/brillo fino): solo en configurados.
    // Blend ADD para que el brillo del marco se note sobre el icono (el asset es muy tenue).
    const iconOverlay = this.makeFx(FX_OVERLAY_KEY, false);
    iconOverlay?.setBlendMode(Phaser.BlendModes.ADD);
    // Cooldown (wipe vertical con spell_frame_cooldown_effect), oculto por defecto.
    const cdImg = this.makeFx(FX_COOLDOWN_KEY, false);
    cdImg?.setOrigin(0.5, 0.5);
    // Glow de hover y efecto de push (ocultos por defecto).
    const hoverFx = this.makeFx(FX_HOVER_KEY, false);
    const pushFx = this.makeFx(FX_PUSH_KEY, false);

    // Cooldown text (unused but kept for interface compat)
    const cdText = this.scene.add.text(0, 0, '', withGameFont({ fontSize: '12px', color: '#ffffff' }))
      .setOrigin(0.5).setVisible(false);

    // Spell name abbreviation (oculto: la referencia muestra solo icono + keycap)
    const label = this.scene.add.text(0, 0, '', withGameFont({ fontSize: '8px', color: '#cccccc', align: 'center' }))
      .setOrigin(0.5).setVisible(false);
    this.container.add(label);

    // Keycap (cajita de tecla = asset) al pie-centro, encima de todo, con el número.
    const keycapImg = this.scene.textures.exists(KEYCAP_FRAME_KEY)
      ? this.scene.add.image(0, 0, KEYCAP_FRAME_KEY).setOrigin(0.5, 0.5)
      : null;
    if (keycapImg) this.container.add(keycapImg);
    const keyHint = this.scene.add.text(0, 0, '', withGameFont({ fontSize: '12px', color: KEYCAP_TEXT, fontStyle: 'bold', resolution: 3 }))
      .setOrigin(0.5, 0.5);
    this.container.add(keyHint);

    // Press: muestra el push fx; release: castea SOLO si el press empezó en este
    // slot (click real). Así soltar un hechizo arrastrado desde la ventana H sobre
    // el slot NO lo castea — solo lo asigna (el drop lo maneja GameScene).
    let pressedHere = false;
    frame.on('pointerdown', (p: Phaser.Input.Pointer) => {
      pressedHere = true;
      pushFx?.setVisible(true);
      // Si el slot tiene hechizo, arrancar el hold para un posible drag (reordenar).
      if (this.slots[index]?.spellId) {
        this.cancelSlotDrag();
        const holdTimer = this.scene.time.delayedCall(SLOT_DRAG_HOLD_MS, () => this.activateSlotDrag(index, p));
        this.slotDrag = { fromIndex: index, active: false, preview: null, holdTimer };
      }
    });
    frame.on('pointerup', (p: Phaser.Input.Pointer) => {
      pushFx?.setVisible(false);
      // Si el drag ya se activó, lo resuelve el pointerup global (handlePointerUp).
      if (this.slotDrag?.active) { pressedHere = false; p.event.stopPropagation(); return; }
      // Release corto (< hold) → cancelar el hold y castear (click real en el slot).
      this.cancelSlotDrag();
      if (pressedHere) {
        pressedHere = false;
        this.castSlot(index);
      }
      p.event.stopPropagation();
    });

    // Hover: glow + tooltip.
    frame.on('pointerover', () => { hoverFx?.setVisible(true); this.showTooltip(index); });
    frame.on('pointerout', () => {
      pressedHere = false;
      // Si salgo del slot con un hold pendiente (aún no activo), cancelarlo.
      if (this.slotDrag && !this.slotDrag.active) this.cancelSlotDrag();
      hoverFx?.setVisible(false); pushFx?.setVisible(false); this.hideTooltip();
    });

    return {
      frame, icon, label, keyHint, keycapImg, iconOverlay, hoverFx, pushFx, cdImg, cdText,
      spellId: null, cdStart: 0, cdDuration: 0,
      x: 0, y: 0, size, width: size,
    };
  }

  /** Crea una imagen de efecto de slot si la textura existe (si no, null). */
  private makeFx(key: string, visible: boolean): Phaser.GameObjects.Image | null {
    if (!this.scene.textures.exists(key)) return null;
    const img = this.scene.add.image(0, 0, key).setOrigin(0.5, 0.5).setVisible(visible);
    this.container.add(img);
    return img;
  }

  private abbreviate(name: string, maxLen: number): string {
    if (name.length <= maxLen) return name;
    return name.substring(0, maxLen);
  }

  private tooltipDesc: Phaser.GameObjects.Text | null = null;

  private createTooltip(): void {
    this.tooltip = this.scene.add.container(0, 0).setDepth(1200).setScrollFactor(0).setVisible(false);
    this.tooltipBg = this.scene.add.rectangle(0, 0, 200, 70, 0x111111, 0.95).setStrokeStyle(1, 0x888888).setOrigin(0.5, 1);
    this.tooltip.add(this.tooltipBg);
    this.tooltipText = this.scene.add.text(0, -60, '', withGameFont({ fontSize: '10px', color: '#ffff88', fontStyle: 'bold' })).setOrigin(0.5, 0);
    this.tooltip.add(this.tooltipText);
    this.tooltipDesc = this.scene.add.text(0, -44, '', withGameFont({ fontSize: '9px', color: '#cccccc', align: 'center', wordWrap: { width: 185 } })).setOrigin(0.5, 0);
    this.tooltip.add(this.tooltipDesc);
  }

  private showTooltip(index: number): void {
    const slot = this.slots[index];
    if (!slot.spellId || !this.tooltip || !this.tooltipText || !this.tooltipDesc || !this.tooltipBg) return;
    const spell = getSpellDefinition(slot.spellId);
    if (!spell) return;

    const keyLabel = toShortKeyLabel(this.inputManager.getActionKeyLabel(SLOT_ACTION_IDS[index]));
    this.tooltipText.setText(`${spell.nameEs}  ${keyLabel}  CD: ${spell.cooldown}s`);
    // Línea compacta con iconos de la criatura invocada (si el hechizo invoca).
    const summon = getSummonDisplayForSpell(spell);
    let desc = spell.descriptionEs;
    if (summon) {
      const roleIcon = summon.role === 'support' ? '✚' : '⚔';
      desc += `\n🐾 ${summon.name}${summon.count > 1 ? ` x${summon.count}` : ''}  ❤${summon.hp}  ${roleIcon}${summon.minDamage}-${summon.maxDamage}  ⚡${summon.attackSpeed.toFixed(1)}  ✦${Math.round(summon.critChance * 100)}%`;
    }
    this.tooltipDesc.setText(desc);

    // Resize bg to fit description
    const descH = this.tooltipDesc.height || 12;
    const totalH = 16 + descH + 10;
    this.tooltipBg.setSize(200, totalH);
    this.tooltipText.setY(-totalH + 6);
    this.tooltipDesc.setY(-totalH + 20);

    this.tooltip.setPosition(slot.x, slot.y - slot.size / 2 - 8);
    this.tooltip.setVisible(true);
  }

  private hideTooltip(): void {
    this.tooltip?.setVisible(false);
  }
}
